import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import { FaSpinner, FaPencilAlt, FaTrash, FaChevronLeft, FaChevronRight, FaHistory } from 'react-icons/fa';
import { supabase } from '../lib/supabase';
import './ArtifactTile.css';

interface Artifact {
  id: string;
  type: string;
  content: string;
  created_at: string;
  project_id: string;
  user_id: string;
  version: number;
}

interface ArtifactTileProps {
  artifact: Artifact;
  onEdit: (artifact: Artifact) => void;
  onDelete: (artifactId: string) => void;
  editingArtifact: string | null;
  editedArtifactContent: string;
  onEditContentChange: (content: string) => void;
  onSave: (artifactId: string, content: string) => void;
  onCancelEdit: () => void;
  deletingArtifact: string | null;
  getArtifactIcon: (type: string) => React.ReactNode;
  isExpanded?: boolean;
  onExpand?: (artifactId: string) => void;
  isFullWidth?: boolean;
}

const ArtifactTile: React.FC<ArtifactTileProps> = ({
  artifact,
  onEdit,
  onDelete,
  editingArtifact,
  editedArtifactContent,
  onEditContentChange,
  onSave,
  onCancelEdit,
  deletingArtifact,
  getArtifactIcon,
  isExpanded = false,
  onExpand,
  isFullWidth = false,
}) => {
  const [showHistory, setShowHistory] = useState(false);
  const [versions, setVersions] = useState<Artifact[]>([]);
  const [currentVersion, setCurrentVersion] = useState<Artifact>(artifact);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (showHistory) {
      loadVersions();
    }
  }, [showHistory]);

  useEffect(() => {
    setCurrentVersion(artifact);
  }, [artifact]);

  const loadVersions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('artifacts')
        .select('*')
        .eq('project_id', artifact.project_id)
        .eq('type', artifact.type)
        .order('version', { ascending: false });

      if (error) throw error;
      if (data) {
        setVersions(data);
      }
    } catch (error) {
      console.error('Error loading versions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVersionChange = (direction: 'prev' | 'next') => {
    const currentIndex = versions.findIndex(v => v.id === currentVersion.id);
    if (direction === 'prev' && currentIndex < versions.length - 1) {
      setCurrentVersion(versions[currentIndex + 1]);
    } else if (direction === 'next' && currentIndex > 0) {
      setCurrentVersion(versions[currentIndex - 1]);
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.artifact-tile-actions, .version-navigation')) {
      return;
    }
    onExpand?.(artifact.id);
  };

  const getPreviewContent = (content: string) => {
    return content.replace(/[#*`]/g, '').slice(0, 150) + '...';
  };

  return (
    <div 
      className={`artifact-tile ${isExpanded ? 'expanded' : ''} ${isFullWidth ? 'full-width' : ''} ${showHistory ? 'showing-history' : ''}`}
      onClick={handleClick}
    >
      <div className="artifact-tile-header">
        <div className="artifact-tile-title">
          <span className="artifact-tile-icon">
            {getArtifactIcon(artifact.type)}
          </span>
          <span>{artifact.type.toUpperCase()}</span>
          <span className="version-label">v{currentVersion.version}</span>
        </div>
        <div className="artifact-tile-actions">
          {isExpanded && (
            <button
              className="history-button"
              onClick={(e) => {
                e.stopPropagation();
                setShowHistory(!showHistory);
              }}
              title={showHistory ? "Hide History" : "View History"}
            >
              <FaHistory />
            </button>
          )}
          {showHistory && versions.length > 1 && (
            <div className="version-navigation" onClick={e => e.stopPropagation()}>
              <button
                className="version-button"
                onClick={() => handleVersionChange('prev')}
                disabled={versions.findIndex(v => v.id === currentVersion.id) === versions.length - 1}
              >
                <FaChevronLeft />
              </button>
              <button
                className="version-button"
                onClick={() => handleVersionChange('next')}
                disabled={versions.findIndex(v => v.id === currentVersion.id) === 0}
              >
                <FaChevronRight />
              </button>
            </div>
          )}
          <button
            className="edit-button"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(currentVersion);
            }}
          >
            <FaPencilAlt />
          </button>
          <button
            className="icon-button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(currentVersion.id);
            }}
            disabled={deletingArtifact === currentVersion.id}
          >
            {deletingArtifact === currentVersion.id ? (
              <FaSpinner className="spinner" />
            ) : (
              <FaTrash />
            )}
          </button>
        </div>
      </div>
      <div className="artifact-tile-date">
        {new Date(currentVersion.created_at).toLocaleString()}
      </div>
      {isFullWidth ? (
        <div className="artifact-tile-content">
          {editingArtifact === currentVersion.id ? (
            <div className="edit-content">
              <textarea
                value={editedArtifactContent}
                onChange={(e) => onEditContentChange(e.target.value)}
                rows={10}
                onClick={e => e.stopPropagation()}
              />
              <div className="edit-actions">
                <button
                  className="save-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSave(currentVersion.id, editedArtifactContent);
                  }}
                >
                  Save
                </button>
                <button
                  className="cancel-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onCancelEdit();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <ReactMarkdown>{currentVersion.content}</ReactMarkdown>
          )}
        </div>
      ) : (
        <div className="artifact-tile-preview">
          {getPreviewContent(currentVersion.content)}
        </div>
      )}
    </div>
  );
};

export default ArtifactTile;
